import React, { useState, useEffect } from 'react';
import { Button, Card, Container, Row, Col, FormGroup, FormControl, FormLabel, Form } from 'react-bootstrap';
import { QrReader } from 'react-qr-reader';
import Swal from 'sweetalert2';
import { getEventPin, handlePinAuthError, requireEventPin } from "../../../../utils/pinAuth";
import withReactContent from 'sweetalert2-react-content';
import { useGuestContext } from "../../../../contexts/GuestContext";
import axios from 'axios';

const MySwal = withReactContent(Swal);

function EventCheckAdminForm() {
    // Ask for the PIN as soon as the station is opened, not at the first action.
    useEffect(() => {
        requireEventPin(`${process.env.REACT_APP_SERVER_URL}/event/verify-checkin-pin`);
    }, []);


    const { language } = useGuestContext();
    const [guestID, setGuestID] = React.useState(null);
    const [firstname, setFirstname] = React.useState(null);
    const [lastname, setLastname] = React.useState(null);
    const [phone, setPhone] = React.useState(null);
    const [email, setEmail] = React.useState(null);
    const [chinesename, setChinesename] = React.useState(null);
    const [userData, setUserData] = React.useState([]);


    const handleScan = (data) => {
        if (data) {
            window.location.href = data; // Redirect to the scanned URL
            MySwal.close(); // Close the SweetAlert modal
        }
    };

    const handleError = (err) => {
        console.error(err);
    };

    const startScan = () => {
        MySwal.fire({
            title: 'Scan QR Code',
            html: <QrReader
                constraints={{
                    facingMode: "environment",
                }}
                key="environment"
                delay={300}
                onError={handleError}
                onResult={(result, error) => {
                    if (!!result) {
                        handleScan(result?.text);
                    }
                    if (!!error) {
                        handleError(error);
                    }
                }}
                style={{ width: '100%' }}
               
            />,
            showConfirmButton: false,
            showCancelButton: true,
            cancelButtonText: 'Cancel',
            willClose: () => {
                // Any cleanup or state management you need to do when the modal closes
            }
        });
    };

    const checkInGuest = () => {
        const guestIdInt = parseInt(guestID, 10); // Parse guestID to an integer

        if (!isNaN(guestIdInt) && guestIdInt > 0) {
            // If guestIdInt is a valid number, proceed with navigation
            const url = `${process.env.REACT_APP_SERVER_URL}/event/checkin?guestid=${guestIdInt}`;
            window.location.href = url;
        } else {
            // If guestIdInt is not valid, show SweetAlert error message
            MySwal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'Please enter a valid Guest ID.',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Ok'
            });
        }
    };

    const performCheckIn = async (guestId) => {
        if (!guestId) {
            MySwal.fire({
                icon: 'error',
                title: 'Oops...',
                text: 'No guest selected for check-in.',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Ok'
            });
            return;
        }

        const url = `${process.env.REACT_APP_SERVER_URL}/event/checkinconfirm?guestid=${guestId}`;

        try {
            const pin = await getEventPin();
            await axios.get(url, { headers: { 'X-Event-Pin': pin } });
            // Handle successful check-in
            MySwal.fire({
                icon: 'success',
                title: 'Check-in Successful',
                text: `Guest ID ${guestId} has been checked in successfully.`,
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Ok',
                willClose: () => {
                    window.location.reload(); // Reload the page when the alert is closed
                }
            });
        } catch (error) {
            if (error.response?.status === 401) {
                handlePinAuthError();
                return;
            }
            // Handle error during check-in
            MySwal.fire({
                icon: 'error',
                title: 'Check-in Failed',
                text: error.response?.data?.detail
                    || 'There was a problem checking in the guest. Please try again.',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Ok'
            });
            console.error('Check-in failed:', error);
        }
    };

    const searchGuest = async () => {
        const trimmedFirstname = firstname ? firstname.trim() : '';
        const trimmedLastname = lastname ? lastname.trim() : '';
        const trimmedEmail = email ? email.trim() : '';
        const trimmedPhone = phone ? phone.trim() : '';

        if (!trimmedFirstname && !trimmedLastname && !trimmedEmail && !trimmedPhone) {
            MySwal.fire({
                icon: 'warning',
                title: 'Oops...',
                text: 'Please fill in at least one field to search for a guest.',
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'Ok'
            });
        } else {
            const payload = {
                firstname: trimmedFirstname,
                lastname: trimmedLastname,
                email: trimmedEmail,
                phone: trimmedPhone
            };

            try {
                const pin = await getEventPin();
                const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/event/searchguest`, payload, {
                    headers: { 'X-Event-Pin': pin }
                });
                setUserData(response.data); // Store the received data in state

                if (response.data && response.data.length > 0) {
                    // Use SweetAlert to display the data
                    MySwal.fire({
                        title: 'Select a Guest to Check-in',
                        html: <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th className="text-center"></th>
                                        <th className="text-center">Guest ID</th>
                                        <th className="text-center">Name</th>
                                        <th className="text-center">Phone</th>
                                        <th className="text-center">Chinese Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {response.data.map((user, index) => (
                                        <tr key={user.id}>
                                            <td className="text-center"><input type="radio" name="userSelection" value={user.id} /></td>
                                            <td className="text-center">{user.id}</td>
                                            <td className="text-center">{`${user.firstname} ${user.lastname}`}</td>
                                            <td className="text-center">{user.phone}</td>
                                            <td className="text-center">{user.chinesename}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>,
                        showCancelButton: true,
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: 'Check-in Selected Guest',
                        cancelButtonText: 'Cancel',
                        preConfirm: () => {
                            const selectedUserId = document.querySelector('input[name="userSelection"]:checked').value;
                            return selectedUserId;
                        }
                    }).then((result) => {
                        if (result.value) {
                            // Handle the check-in process for the selected user
                            console.log("Selected user ID for check-in:", result.value);
                            performCheckIn(result.value);
                        }
                    });
                } else {
                    MySwal.fire({
                        icon: 'info',
                        title: 'No Matches',
                        text: 'No matching guests found.',
                        confirmButtonColor: '#3085d6',
                        confirmButtonText: 'Ok'
                    });
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    handlePinAuthError();
                    return;
                }
                MySwal.fire({
                    icon: 'error',
                    title: 'Failed to Search',
                    text: 'There was a problem searching for the guest. Please try again.',
                    confirmButtonColor: '#3085d6',
                    confirmButtonText: 'Ok'
                });
                console.error('Search guest failed:', error);
            }
        }
    };


    const handleExpressRegistration = () => {
        MySwal.fire({
            title: 'Express Registration',
            html: `
                <input id="swal-input1" class="swal2-input" placeholder="First Name">
                <input id="swal-input2" class="swal2-input" placeholder="Last Name">
            `,
            focusConfirm: false,
            showCancelButton: true, // Allow user to cancel the action
            confirmButtonText: 'Submit', // Text for the confirm button
            cancelButtonText: 'Cancel', // Text for the cancel button
            preConfirm: () => {
                const firstName = document.getElementById('swal-input1').value.trim();
                const lastName = document.getElementById('swal-input2').value.trim();
                
                if (!firstName || !lastName) {
                    MySwal.showValidationMessage('Both first name and last name are required');
                    return false;
                }
                
                return { firstName, lastName };
            }
        }).then((result) => {
            if (result.isConfirmed && result.value) {
                // Perform the API call to register the user
                getEventPin().then(pin => axios.post(`${process.env.REACT_APP_SERVER_URL}/event/expressregistration`, {
                    firstname: result.value.firstName,
                    lastname: result.value.lastName
                }, { headers: { 'X-Event-Pin': pin } }))
                .then(response => {
                    // Handle the success response here
                    MySwal.fire({
                        icon: 'success',
                        title: 'Registration Successful',
                        text: `The guest has been successfully registered and checked in.`,
                    });
                })
                .catch(error => {
                    if (error.response?.status === 401) {
                        handlePinAuthError();
                        return;
                    }
                    // Handle the error response here
                    MySwal.fire({
                        icon: 'error',
                        title: 'Registration Failed',
                        text: error.response?.data?.detail
                            || 'There was a problem with the registration. Please try again.',
                    });
                    console.error('Express registration failed:', error);
                });
            }
        });
    };
    

    return (
        <>
            <Container fluid>
                <Row>
                    <Col md="12">
                        <Card>
                            <Card.Header>
                                <Card.Title as="h4" className="font-weight-bold text-center mb-2">梁皇寶懺精進共修報到</Card.Title>
                                <hr />
                            </Card.Header>
                            <Card.Body>
                                <Row className='mb-3'>
                                    <Col md="12" className="text-center">
                                        <h6 className='text-center'>Scenario 1: QR Code Checkin</h6>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="12" className="text-center">
                                        <Button className="btn-fill font-weight-bold" variant="info" onClick={startScan}>
                                            Scan QR Code
                                        </Button>
                                    </Col>
                                </Row>
                                <hr />
                                <Row className='mb-3'>
                                    <Col md="12" className="text-center">
                                        <h6 className='text-center'>Scenario 2: Guest ID Checkin</h6>
                                    </Col>
                                </Row>

                                <Row className='mb-3 text-center'>
                                    <Col md={{ span: 10, offset: 1 }}>
                                        <FormGroup>
                                            <FormLabel>
                                                <h6 className="font-weight-bold">Guest ID</h6>
                                            </FormLabel>
                                            <FormControl
                                                type="number"
                                                name="guestID"
                                                placeholder="ex: 12345"
                                                value={guestID}
                                                onChange={(event) => setGuestID(event.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="12" className="text-center">
                                        <Button className="btn-fill font-weight-bold" variant="success" onClick={checkInGuest}>
                                            Check-in Guest
                                        </Button>
                                    </Col>
                                </Row>
                                <hr />
                                <Row className='mb-3'>
                                    <Col md="12" className="text-center">
                                        <h6 className='text-center'>Scenario 3: Search Guest</h6>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={{ span: 5, offset: 1 }}>
                                        <FormGroup>
                                            <FormLabel>
                                                {language === "English" ? "First Name" : "英文名"}
                                            </FormLabel>
                                            <FormControl
                                                type="text"
                                                name="firstname"
                                                placeholder="ex: Mike"
                                                value={firstname}
                                                onChange={(event) => setFirstname(event.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={5}>
                                        <FormGroup>
                                            <FormLabel>
                                                {language === "English" ? "Last Name " : "英文姓"}
                                            </FormLabel>
                                            <FormControl
                                                type="text"
                                                name="lastname"
                                                placeholder="ex: Andrew"
                                                value={lastname}
                                                onChange={(event) => setLastname(event.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={{ span: 5, offset: 1 }}>
                                        <FormGroup>
                                            <FormLabel>
                                                {language === "English" ? "Phone Number " : "電 話 號 碼"}
                                            </FormLabel>
                                            <FormControl
                                                type="number"
                                                name="phone"
                                                maxLength="30"
                                                placeholder="ex: 0433233343"
                                                value={phone}
                                                onChange={(event) => setPhone(event.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={5}>
                                        <FormGroup>
                                            <FormLabel>
                                                {language === "English" ? "Email" : "電 子 郵 件"}
                                            </FormLabel>
                                            <FormControl
                                                type="email"
                                                name="email"
                                                maxLength="50"
                                                placeholder="ex: wukong@baolin.com"
                                                value={email}
                                                onChange={(event) => setEmail(event.target.value)}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="12" className="text-center mt-2">
                                        <Button className="btn-fill font-weight-bold" variant="warning" onClick={searchGuest}>
                                            Search Guest
                                        </Button>
                                    </Col>
                                </Row>
                                {/* <hr />
                                <Row>
                                    <Col md="12" className="text-center mt-2">
                                        <Row className='mb-3'>
                                            <Col md="12" className="text-center">
                                                <h6 className='text-center'>Scenario 4: Walk-in Guest</h6>
                                            </Col>
                                        </Row>
                                        <Button className="btn-fill font-weight-bold" variant="primary" onClick={handleExpressRegistration}>
                                            Express Registration
                                        </Button>
                                    </Col>
                                </Row> */}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
}

export default EventCheckAdminForm;
